import { useState, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import {
  getCurrentLocation,
  isGPSAvailable,
  isWithinPropertyRadius,
  calculateDistance,
  formatDistance,
} from '../../../services/location.service';

const DEFAULT_THRESHOLD_METERS = 75;

/**
 * Hook for GPS tracking on work orders
 * Handles location capture, verification, and distance calculations
 * 
 * @param {Object} propertyLocation - Property coordinates {latitude, longitude}
 * @param {number} thresholdMeters - Distance threshold in meters (default: 75)
 * @returns {Object} GPS state and functions
 */
export function useWorkOrderGPS(propertyLocation, thresholdMeters = DEFAULT_THRESHOLD_METERS) {
  const [currentLocation, setCurrentLocation] = useState(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [captureError, setCaptureError] = useState(null);
  const [showDistanceWarning, setShowDistanceWarning] = useState(false);

  const gpsAvailable = isGPSAvailable();

  /**
   * Capture current GPS location
   * @param {boolean} showWarning - Whether to show warning if far from property
   * @returns {Promise<Object|null>} Location object or null if failed
   */
  const captureLocation = useCallback(async (showWarning = true) => {
    if (!gpsAvailable) {
      toast.error('GPS is not available on this device. Please enable location services.');
      setCaptureError('GPS not available');
      return null;
    }

    setIsCapturing(true);
    setCaptureError(null);

    try {
      const location = await getCurrentLocation({
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 60000,
      });

      setCurrentLocation(location);

      // Check distance from property if property coordinates are available
      if (showWarning && propertyLocation?.latitude && propertyLocation?.longitude) {
        const distance = calculateDistance(
          location.latitude,
          location.longitude,
          propertyLocation.latitude,
          propertyLocation.longitude
        );

        if (distance > thresholdMeters) {
          setShowDistanceWarning(true);
          // Don't block - let user continue anyway
          toast(
            `You are ${formatDistance(distance)} from the property. You can still continue.`,
            { 
              duration: 5000,
              icon: '⚠️',
            }
          );
        } else {
          setShowDistanceWarning(false);
        }
      }

      return location;
    } catch (error) {
      console.error('GPS capture error:', error);
      setCaptureError(error.message);
      
      // Don't block if GPS fails - allow continuation
      toast(
        `Could not capture GPS location: ${error.message}. You can still continue.`,
        { 
          duration: 5000,
          icon: '⚠️',
        }
      );
      
      return null;
    } finally {
      setIsCapturing(false);
    }
  }, [gpsAvailable, propertyLocation, thresholdMeters]);

  /**
   * Clear captured location
   */
  const clearLocation = useCallback(() => {
    setCurrentLocation(null);
    setCaptureError(null);
    setShowDistanceWarning(false);
  }, []);

  /**
   * Get distance from property in meters
   * @returns {number|null} Distance in meters or null
   */
  const distanceFromProperty = currentLocation && propertyLocation?.latitude && propertyLocation?.longitude
    ? calculateDistance(
        currentLocation.latitude,
        currentLocation.longitude,
        propertyLocation.latitude,
        propertyLocation.longitude
      )
    : null;

  /**
   * Check if current location is within property radius
   * @returns {boolean} true if within radius
   */
  const isAtProperty = currentLocation && propertyLocation?.latitude && propertyLocation?.longitude
    ? isWithinPropertyRadius(
        currentLocation.latitude,
        currentLocation.longitude,
        propertyLocation.latitude,
        propertyLocation.longitude,
        thresholdMeters
      )
    : false;

  return {
    currentLocation,
    isCapturing,
    captureError,
    showDistanceWarning,
    gpsAvailable,
    distanceFromProperty,
    isAtProperty,
    captureLocation,
    clearLocation,
    formatDistance: distanceFromProperty ? formatDistance(distanceFromProperty) : null,
  };
}
